import { AgentType, AgentInteractionResult, AssistantConfig, APIAttachment, DEFAULT_ASSISTANT_AGENT_NAME, ApiDocumentReference, ArdoqId, ChatCancellationResponse, ChatSessionExport, HistoricChat, InteractionRequest, LoadedChat, } from '@ardoq/api-types';
import { CSRF_HEADER_NAME, Method, deleteEntity, fetchEntities, fetchJson, fetchVoid, getCsrfToken, toRequestOptions, updateEntity, } from './api';
import { ApiResponse } from './types';
const getUserUrl = (): string => '/api/attachment/user';
const getItemFromUserUrl = (userId: ArdoqId, itemId: ArdoqId): string => `${getUserUrl()}/${userId}/${itemId}`;
/** One applicable AI-controlled command as advertised to the assistant. */
export type UiCommandManifestEntry = {
    /** Stable wire id the assistant calls the command by. */
    name: string;
    /** The asset area the command operates in (e.g. 'viewpoint'). */
    domain: string;
    /** What it does + when to use it — the assistant's selection signal. */
    description: string;
    /** Full JSON Schema envelope for the payload (`typia.json.schema` output). */
    parameters: object;
    /** The command's live on-screen state, revealed lazily to the sub-agent. */
    context: object | null;
    /** How to interpret `context`. */
    contextDescription: string;
};
/** One entry of the per-view domain menu — an asset area plus its description.
 * Lets the main agent classify a request without touching the cached prefix. */
export type DomainMenuEntry = {
    domain: string;
    description: string;
};
/** One action the frontend offers the user, advertised so the assistant can offer it back
 * as a button. The `label` is already translated by the frontend. */
export type OfferableActionManifestEntry = {
    /** Stable wire id the assistant names the action by. */
    name: string;
    /** The user-facing button text, already translated. */
    label: string;
    /** When to offer it — the assistant's sole selection signal. */
    description: string;
    /** JSON Schema of the action's props payload (`typia.json.schema` output).
     * Absent for payload-free actions (the model only names them). */
    parameters?: object;
};
/** One workflow as advertised to the assistant: an ordered sequence of AI-controlled commands. */
export type UiWorkflowManifestEntry = {
    /** Stable wire id the assistant calls the workflow by. */
    name: string;
    /** Wire ids of the commands the workflow runs, in order. */
    commandsIds: string[];
    /** What it does + when to use it — the assistant's selection signal. */
    description: string;
};
/** Versioned render input schemas (`typia.json.schema` output), keyed by render type.
 * `viewPreview` is sent only while the assistant view preview feature is on. */
export type RenderSchemasRequestPayload = {
    chart: {
        version: 1;
        schema: object;
    };
    viewPreview?: {
        version: 1;
        schema: object;
    };
};
/** The versioned `uiCommands` request payload advertised by the frontend.
 * Carries the applicable commands and the domain menu. The asset identity the commands act on
 * comes from the per-message `assetContext`, not from here. */
export type UiCommandsRequestPayload = {
    version: 1;
    commands: UiCommandManifestEntry[];
    /** Only the workflows whose first command is currently applicable. */
    workflows: UiWorkflowManifestEntry[];
    domains: DomainMenuEntry[];
    actions?: OfferableActionManifestEntry[];
    renderSchemas: RenderSchemasRequestPayload;
};
export type SendMessageRequest = {
    content: string;
    reportId?: string | null;
    chatSessionId?: string | null;
    clientRequestId: string;
    agentType?: AgentType;
    assistantConfig?: AssistantConfig;
    context?: object & {
        assetType: string;
        _id?: string;
    };
    uiCommands?: UiCommandsRequestPayload;
    documents?: ApiDocumentReference[];
    webSearchEnabled?: boolean;
};
/** The asset the interaction result is authorized and applied against. Mirrors the
 * per-message `context` shape sent with a normal chat turn. */
export type AssistantAssetContext = object & {
    assetType: string;
    _id?: string;
};
/** The `agent-interaction-requested` websocket event payload. */
export type AgentInteractionRequested = {
    interactionId: string;
    exchangeId: string;
    timestamp: string;
    request: InteractionRequest;
};
/** The `agent-interaction-resolved` websocket event payload. */
export type AgentInteractionResolved = {
    interactionId: string;
    exchangeId: string;
    result: AgentInteractionResult;
};
export type ResolveInteractionRequest = {
    exchangeId: string;
    interactionId: string;
    result: AgentInteractionResult;
    assetContext: AssistantAssetContext;
    uiCommands: UiCommandsRequestPayload;
};
/** ardoq-api's acknowledgement that the interaction reached a terminal state and a
 * continuation run was queued. */
export type ResolveInteractionResponse = {
    interactionId: string;
    exchangeId: string;
    status: 'resolved';
};
const sendMessageWithHistory = ({ content, clientRequestId, chatSessionId, agentType, context, assistantConfig, uiCommands, documents, webSearchEnabled, }: SendMessageRequest): ApiResponse<{
    session: HistoricChat;
    exchangeId: string;
}> => {
    const body = {
        newMessage: content,
        chatSessionId,
        assetContext: context,
        uiCommands,
        // For Front end we only set the agent type when actual agent is in use.
        // the ardoq_assistant is not an agent but we need this fallback value for backend api.
        agentType: agentType ?? assistantConfig?.agent ?? DEFAULT_ASSISTANT_AGENT_NAME,
        documents,
        webSearchEnabled,
        ...(assistantConfig && { model: assistantConfig.model }),
    };
    const request = new Request(`/api/chat/complete/v2?requestId=${clientRequestId}`, toRequestOptions(Method.POST, body));
    return fetchJson(request);
};
const sendDurableCancellation = ({ chatSessionId, }: {
    chatSessionId: string;
}): ApiResponse<ChatCancellationResponse> => {
    const request = new Request(`/api/chat/cancel-durable/${chatSessionId}`, toRequestOptions(Method.POST, {}));
    return fetchJson<ChatCancellationResponse>(request);
};
/** Submit the authoritative result of a durable UI-command interaction. ardoq-api folds
 * it into the interaction's lifecycle, and — for the winning result — queues the
 * continuation run that resumes the paused specialist. Equivalent retries are idempotent;
 * a conflicting result is rejected by the backend. */
const resolveInteraction = ({ exchangeId, interactionId, result, assetContext, uiCommands, }: ResolveInteractionRequest): ApiResponse<ResolveInteractionResponse> => {
    const request = new Request(`/api/chat/exchanges/${exchangeId}/interactions/${interactionId}`, toRequestOptions(Method.PUT, { result, assetContext, uiCommands }));
    return fetchJson<ResolveInteractionResponse>(request);
};
const pinHistoricChat = async (chat: HistoricChat): ApiResponse<HistoricChat> => {
    return updateEntity<HistoricChat>('chat/session')({
        ...chat,
        pinned: true,
    });
};
const unpinHistoricChat = async (chat: HistoricChat): ApiResponse<HistoricChat> => {
    return updateEntity<HistoricChat>('chat/session')({
        ...chat,
        pinned: false,
    });
};
type RenameHistoricChatArgs = {
    chat: HistoricChat;
    title: string;
};
const renameHistoricChat = ({ chat, title, }: RenameHistoricChatArgs): ApiResponse<HistoricChat> => updateEntity<HistoricChat>('chat/session')({ ...chat, title });
const fetchHistoricChatContent = (id: string): ApiResponse<LoadedChat> => {
    const request = new Request(`/api/chat/session/${id}/messages`);
    return fetchJson<LoadedChat>(request);
};
const markChatNotificationRead = (chatSessionId: string): ApiResponse<void> => {
    const request = new Request(`/api/chat/session/${chatSessionId}/notification/read`, toRequestOptions(Method.POST, {}));
    return fetchVoid(request);
};
const fetchChatSessionExport = (id: string): ApiResponse<ChatSessionExport> => {
    const request = new Request(`/api/chat/session/${id}/internal/export`);
    return fetchJson<ChatSessionExport>(request);
};
type UploadUserDocumentPayload = {
    file: File;
    chatSessionId?: string | null;
};
const uploadUserDocument = ({ file, chatSessionId, }: UploadUserDocumentPayload): ApiResponse<APIAttachment> => {
    const url = chatSessionId
        ? `${getUserUrl()}/upload?chatSessionId=${chatSessionId}`
        : `${getUserUrl()}/upload`;
    const token = getCsrfToken();
    const csrfHeader = token ? { [CSRF_HEADER_NAME]: token } : undefined;
    const formData = new FormData();
    formData.append('attachment', file);
    return fetchJson<APIAttachment>(new Request(url, {
        method: Method.POST,
        headers: csrfHeader,
        body: formData,
    }));
};
const deleteUserDocument = ({ userId, documentId, }: {
    userId: ArdoqId;
    documentId: ArdoqId;
}): ApiResponse<void> => {
    const token = getCsrfToken();
    const csrfHeader = token ? { [CSRF_HEADER_NAME]: token } : undefined;
    return fetchVoid(new Request(getItemFromUserUrl(userId, documentId), {
        method: Method.DELETE,
        headers: csrfHeader,
    }));
};
export const assistantApi = {
    sendMessageWithHistory,
    sendDurableCancellation,
    resolveInteraction,
    fetchHistoricChats: fetchEntities<HistoricChat>('chat/session'),
    fetchHistoricChatContent,
    markChatNotificationRead,
    fetchChatSessionExport,
    deleteHistoricChat: deleteEntity('chat/session'),
    pinHistoricChat,
    unpinHistoricChat,
    renameHistoricChat,
    uploadUserDocument,
    deleteUserDocument,
    getUserUrl,
    getItemFromUserUrl,
};
